import {Component, OnInit} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {ActivatedRoute, Router} from '@angular/router';
import {finalize} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {BusinessDirectoryService} from '../api/business-directory.service';
import {ChapterApiService} from '../api/chapter-api.service';
import {BusinessComment, BusinessDirectory, BusinessEndorsement, BusinessInquiry} from '../models/business-directory';
import {SpinnerService} from '../shared/services/spinner.service';
import {categoryName, initialsOf, lookingForLabel, serviceAreaLabel, sizeLabel} from '../biz-directory/biz-categories';

/** One listing: profile, contact details, gallery, similar businesses, and the owner/moderator actions. */
@Component({
    selector: 'app-biz-detail',
    templateUrl: './biz-detail.component.html',
    styleUrls: ['./biz-detail.component.css']
})
export class BizDetailComponent implements OnInit {
    biz: BusinessDirectory | null = null;
    // The API sanitizes descriptions on save (scripts and handlers stripped), so inline editor styles are safe to keep.
    description: SafeHtml = '';
    similar: BusinessDirectory[] = [];
    chapterName = '';
    notFound = false;
    loggedIn = sessionStorage.getItem('username') !== null;
    isMember = !!JSON.parse(sessionStorage.getItem('rolesPermission') || '{}')?.BUSINESS?.includes('CREATE');

    comments: BusinessComment[] = [];
    newComment = '';
    endorsements: BusinessEndorsement[] = [];
    endorseOpen = false;
    endorseNote = '';
    inquiries: BusinessInquiry[] = [];      // owner only
    inquiryKind: 'GENERAL' | 'MENTOR' = 'GENERAL';
    inquiryText = '';
    inquirySent = false;
    startedInquiry = false;

    category = categoryName;
    initials = initialsOf;
    areaLabel = serviceAreaLabel;
    sizeLabel = sizeLabel;
    lookingFor = lookingForLabel;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private bizService: BusinessDirectoryService,
        private chapterApi: ChapterApiService,
        private spinner: SpinnerService,
        private toastr: ToastrService,
        private sanitizer: DomSanitizer
    ) {
    }

    ngOnInit(): void {
        this.route.paramMap.subscribe(params => {
            const id = params.get('id');
            if (id) this.load(id);
        });
    }

    private load(id: string): void {
        this.notFound = false;
        this.spinner.show();
        this.bizService.getBusinessById(id).pipe(finalize(() => this.spinner.hide())).subscribe({
            next: biz => {
                this.biz = biz;
                this.description = this.sanitizer.bypassSecurityTrustHtml(biz.description || '');
                this.loadChapterName(biz.ownerChapterId);
                this.loadEngagement(id, biz);
                this.bizService.getSimilar(id).subscribe({
                    next: list => this.similar = list,
                    error: err => console.error('Failed to load similar businesses', err)
                });
            },
            error: err => {
                console.error('Failed to load business', err);
                this.notFound = true;
            }
        });
    }

    private loadEngagement(id: string, biz: BusinessDirectory): void {
        this.inquirySent = false;
        this.bizService.getComments(id).subscribe({
            next: list => this.comments = list,
            error: err => console.error('Failed to load comments', err)
        });
        this.bizService.getEndorsements(id).subscribe({
            next: list => {
                this.endorsements = list;
                const mine = list.find(e => e.userId === sessionStorage.getItem('userId'));
                this.endorseNote = mine?.note || '';
            },
            error: err => console.error('Failed to load endorsements', err)
        });
        if (biz.canEdit) {
            this.bizService.getInquiries(id).subscribe({
                next: list => this.inquiries = list,
                error: err => console.error('Failed to load inquiries', err)
            });
        }
    }

    get canEndorse(): boolean {
        return this.isMember && !!this.biz && !this.biz.canEdit;
    }

    private requireLogin(): boolean {
        if (this.loggedIn) return true;
        this.router.navigate(['/login']);
        return false;
    }

    toggleLike(): void {
        if (!this.biz || !this.requireLogin()) return;
        const biz = this.biz;
        const request = biz.likedByMe ? this.bizService.unlike(biz.id!) : this.bizService.like(biz.id!);
        request.subscribe({
            next: () => {
                biz.likeCount = (biz.likeCount || 0) + (biz.likedByMe ? -1 : 1);
                biz.likedByMe = !biz.likedByMe;
            },
            error: err => this.fail('Could not update your like', err)
        });
    }

    toggleFollow(): void {
        if (!this.biz || !this.requireLogin()) return;
        const biz = this.biz;
        const request = biz.followedByMe ? this.bizService.unfollow(biz.id!) : this.bizService.follow(biz.id!);
        request.subscribe({
            next: () => {
                biz.followerCount = (biz.followerCount || 0) + (biz.followedByMe ? -1 : 1);
                biz.followedByMe = !biz.followedByMe;
                this.toastr.success(biz.followedByMe ? 'You are following this business' : 'You stopped following this business');
            },
            error: err => this.fail('Could not update follow', err)
        });
    }

    saveEndorsement(): void {
        if (!this.biz) return;
        const biz = this.biz;
        this.bizService.endorse(biz.id!, this.endorseNote).subscribe({
            next: () => {
                if (!biz.endorsedByMe) biz.endorsementCount = (biz.endorsementCount || 0) + 1;
                biz.endorsedByMe = true;
                this.endorseOpen = false;
                this.toastr.success('Thank you for recommending this business');
                this.bizService.getEndorsements(biz.id!).subscribe(list => this.endorsements = list);
            },
            error: err => this.fail('Could not save your endorsement', err)
        });
    }

    removeEndorsement(): void {
        if (!this.biz) return;
        const biz = this.biz;
        this.bizService.removeEndorsement(biz.id!).subscribe({
            next: () => {
                biz.endorsementCount = Math.max(0, (biz.endorsementCount || 1) - 1);
                biz.endorsedByMe = false;
                this.endorseNote = '';
                this.endorseOpen = false;
                this.bizService.getEndorsements(biz.id!).subscribe(list => this.endorsements = list);
            },
            error: err => this.fail('Could not remove your endorsement', err)
        });
    }

    postComment(): void {
        const text = this.newComment.trim();
        if (!this.biz || !text) return;
        this.bizService.addComment(this.biz.id!, text).subscribe({
            next: comment => {
                this.comments = [...this.comments, comment];
                this.biz!.commentCount = (this.biz!.commentCount || 0) + 1;
                this.newComment = '';
            },
            error: err => this.fail('Could not post your comment', err)
        });
    }

    deleteComment(comment: BusinessComment): void {
        if (!this.biz || !confirm('Delete this comment?')) return;
        this.bizService.deleteComment(this.biz.id!, comment.commentId).subscribe({
            next: () => {
                this.comments = this.comments.filter(c => c.commentId !== comment.commentId);
                this.biz!.commentCount = Math.max(0, (this.biz!.commentCount || 1) - 1);
            },
            error: err => this.fail('Could not delete the comment', err)
        });
    }

    startInquiry(kind: 'GENERAL' | 'MENTOR'): void {
        if (!this.requireLogin()) return;
        this.inquiryKind = kind;
        this.inquirySent = false;
        this.startedInquiry = true;
        if (!this.inquiryText && kind === 'MENTOR') {
            this.inquiryText = 'Hello, I am thinking of starting a similar business and would value a short conversation about how you got started.';
        }
        setTimeout(() => document.getElementById('biz-inquiry')?.scrollIntoView({behavior: 'smooth', block: 'center'}), 0);
    }

    sendInquiry(): void {
        const message = this.inquiryText.trim();
        if (!this.biz || message.length < 10) {
            this.toastr.error('Please write a short message (at least 10 characters)');
            return;
        }
        this.spinner.show();
        this.bizService.sendInquiry(this.biz.id!, this.inquiryKind, message).pipe(finalize(() => this.spinner.hide())).subscribe({
            next: () => {
                this.inquirySent = true;
                this.inquiryText = '';
                this.toastr.success('Your message was sent to the owner');
            },
            error: err => this.fail('Could not send your message', err)
        });
    }

    private fail(fallback: string, err: any): void {
        console.error(fallback, err);
        this.toastr.error(err?.error?.message || fallback);
    }

    private loadChapterName(chapterId?: string): void {
        this.chapterName = '';
        if (!chapterId) return;
        this.chapterApi.getData().subscribe({
            next: (list: any[]) => this.chapterName = (list || []).find(c => c.id === chapterId)?.name || '',
            error: err => console.error('Failed to load chapters', err)
        });
    }

    get location(): string {
        return [this.biz?.city, this.biz?.state].filter(Boolean).join(', ');
    }

    get hasContact(): boolean {
        const b = this.biz;
        return !!(b && (b.phone || b.email || b.whatsapp || b.linkedin || b.instagram || b.facebook));
    }

    get whatsappLink(): string {
        return 'https://wa.me/' + (this.biz?.whatsapp || '').replace(/[^0-9]/g, '');
    }

    get shareLink(): string {
        return window.location.origin + '/biz-directory/' + this.biz?.id;
    }

    get whatsappShare(): string {
        return 'https://wa.me/?text=' + encodeURIComponent(`${this.biz?.businessName} on the RANA Business Directory: ${this.shareLink}`);
    }

    copyLink(): void {
        navigator.clipboard?.writeText(this.shareLink).then(
            () => this.toastr.success('Link copied'),
            () => this.toastr.info(this.shareLink)
        );
    }

    trackWebsiteClick(): void {
        if (this.biz?.id) this.bizService.recordClick(this.biz.id).subscribe({error: () => { /* a missed click count is harmless */ }});
    }

    toggleHidden(): void {
        if (!this.biz) return;
        const hiding = this.biz.status !== 'HIDDEN';
        const reason = hiding ? prompt('Reason for hiding this listing (optional):') : null;
        if (hiding && reason === null) return;
        const request = hiding ? this.bizService.hide(this.biz.id!, reason || undefined) : this.bizService.unhide(this.biz.id!);
        request.subscribe({
            next: updated => {
                this.biz = {...this.biz!, status: updated.status, hiddenReason: updated.hiddenReason};
                this.toastr.success(hiding ? 'Listing hidden' : 'Listing is visible again');
            },
            error: err => {
                console.error('Moderation failed', err);
                this.toastr.error('Could not update the listing');
            }
        });
    }

    toggleVerified(): void {
        if (!this.biz) return;
        const verifying = !this.biz.verified;
        const request = verifying ? this.bizService.verify(this.biz.id!) : this.bizService.unverify(this.biz.id!);
        request.subscribe({
            next: updated => {
                this.biz = {...this.biz!, verified: updated.verified};
                this.toastr.success(verifying ? 'Marked as verified' : 'Verification removed');
            },
            error: err => {
                console.error('Verification failed', err);
                this.toastr.error('Could not update verification');
            }
        });
    }

    deleteBusiness(): void {
        if (!this.biz || !confirm('Delete this listing permanently?')) return;
        this.bizService.deleteBusiness(this.biz.id!).subscribe({
            next: () => {
                this.toastr.success('Listing deleted');
                this.router.navigate(['/biz-directory']);
            },
            error: err => {
                console.error('Delete failed', err);
                this.toastr.error('Could not delete the listing');
            }
        });
    }
}
